import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard, Sun, Moon, LogOut, ChevronDown, ChevronRight, Menu, X,
    Compass, Hammer, ClipboardCheck, Wallet, BrainCircuit, Settings as SettingsIcon,
    Target, MapPin, Users, HardHat, FileText, Camera, Receipt, PiggyBank, Sparkles, BarChart3, Search,
    Kanban, Contact2, Bell,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useBranding } from "@/context/BrandingContext";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import CommandPalette from "@/components/CommandPalette";
import InstallForgeOSButton from "@/components/InstallForgeOSButton";
import api from "@/lib/api";

const ICON_MONOGRAM = "/brand/icon/df-192.png";

/**
 * Six workspaces. Forge Sales expands into "Pipeline (Kanban)" and "Contacts"
 * so demo owner can reach CRM screens directly from the sidebar (F2 fix).
 */
const WORKSPACES = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/app/dashboard", modules: [] },
    { key: "development", label: "Development", icon: Compass, modules: [
        { key: "forge-vision", label: "Forge Vision", subtitle: "Deals & Analyzer", icon: Target, to: "/app/deals" },
        { key: "forge-sites", label: "Forge Sites", subtitle: "Land & Properties", icon: MapPin, to: "/app/properties" },
        { key: "forge-sales-kanban", label: "Forge Sales · Pipeline", subtitle: "Kanban board", icon: Kanban, to: "/app/crm" },
        { key: "forge-sales-contacts", label: "Forge Sales · Contacts", subtitle: "People & companies", icon: Contact2, to: "/app/contacts" },
    ]},
    { key: "projects", label: "Projects", icon: Hammer, modules: [
        { key: "forge-projects", label: "Forge Projects", subtitle: "Workspace", icon: HardHat, to: "/app/projects" },
        { key: "forge-build", label: "Forge Build", subtitle: "Schedule & tasks", icon: Hammer, to: "/app/build" },
        { key: "forge-docs", label: "Forge Docs", subtitle: "Plans & drawings", icon: FileText, to: "/app/docs" },
    ]},
    { key: "operations", label: "Operations", icon: ClipboardCheck, modules: [
        { key: "forge-field", label: "Forge Field", subtitle: "Daily logs & photos", icon: Camera, to: "/app/field" },
        { key: "permits", label: "Permits & Inspections", icon: ClipboardCheck, to: "/app/permits" },
        { key: "jurisdictions", label: "Jurisdictions", subtitle: "Reference library", icon: MapPin, to: "/app/admin/jurisdictions" },
    ]},
    { key: "business", label: "Business", icon: Wallet, modules: [
        { key: "forge-investors", label: "Investors", subtitle: "Equity partners", icon: PiggyBank, to: "/app/business/investors" },
        { key: "forge-lenders", label: "Lenders", subtitle: "Debt partners", icon: Wallet, to: "/app/business/lenders" },
        { key: "forge-finance", label: "Forge Finance", subtitle: "Budgets & invoices", icon: Receipt, to: "/app/finance" },
    ]},
    { key: "intelligence", label: "Intelligence", icon: BrainCircuit, modules: [
        { key: "forge-ai", label: "Forge AI", subtitle: "Assistants", icon: Sparkles, to: "/app/ai" },
        { key: "forge-analytics", label: "Forge Analytics", subtitle: "Reports & KPIs", icon: BarChart3, to: "/app/reports" },
        { key: "forge-studio", label: "Forge Studio", subtitle: "Coming soon", icon: Sparkles, to: "/app/studio" },
    ]},
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const { organization, software } = useBranding();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set(["development", "intelligence"]));
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const nav = useNavigate();
    const loc = useLocation();

    // Global keyboard: Cmd/Ctrl+K opens palette; Esc closes
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
            if (e.key === "Escape") setPaletteOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Poll unread notifications every 60s
    useEffect(() => {
        let alive = true;
        const load = () => api.get("/notifications?unread_only=true&limit=1").then(({ data }) => { if (alive) setUnread(data.unread_count || 0); }).catch(() => {});
        load();
        const iv = setInterval(load, 60000);
        return () => { alive = false; clearInterval(iv); };
    }, []);

    const companyName = organization?.company_name || "The Dream Forge Company";
    const productName = software?.product_name || "ForgeOS";

    const toggleExpanded = (key) => {
        setExpanded((s) => {
            const next = new Set(s);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const swatchColor = organization?.primary_color || "#0F2747";

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="h-14 border-b border-border flex items-center justify-between px-4 md:px-6 sticky top-0 bg-background/95 backdrop-blur z-40" data-testid="app-topbar">
                <div className="flex items-center gap-3">
                    <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="app-sidebar-toggle" aria-label="Menu">
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                    <Link to="/app/dashboard" className="flex items-center gap-2.5" data-testid="app-topbar-home">
                        <div className="h-5 w-5" style={{ backgroundColor: swatchColor }} data-testid="app-topbar-brand-swatch" />
                        <span className="text-sm font-medium tracking-tight" data-testid="app-topbar-brand-name">{companyName}</span>
                        <span className="text-xs text-muted-foreground hidden md:inline">· {productName}</span>
                    </Link>
                </div>

                <div className="hidden md:flex items-center gap-2 flex-1 max-w-md mx-6">
                    <button onClick={() => setPaletteOpen(true)} className="w-full flex items-center gap-2 border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary/60 transition-colors" data-testid="app-global-search">
                        <Search size={12} />
                        <span className="flex-1 text-left">Search projects, deals, contacts…</span>
                        <kbd className="text-[9px] border border-border px-1 py-0.5">⌘K</kbd>
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <button onClick={() => nav("/app/notifications")} className="relative p-2 hover:opacity-70" data-testid="app-notifications-bell" aria-label="Notifications">
                        <Bell size={15} />
                        {unread > 0 && (
                            <span className="absolute top-1 right-1 min-w-[14px] h-[14px] bg-red-600 text-white text-[9px] rounded-full flex items-center justify-center px-1" data-testid="notif-unread-count">{unread > 9 ? "9+" : unread}</span>
                        )}
                    </button>
                    <button onClick={toggle} className="p-2 hover:opacity-70" data-testid="app-theme-toggle" aria-label="Toggle theme">
                        {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
                    </button>
                    <DropdownMenu>
                        <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 border border-border px-2.5 py-1.5 text-xs hover:bg-secondary transition-colors">
                            <span className="h-6 w-6 bg-secondary flex items-center justify-center text-xs">{(user?.first_name || "?").charAt(0)}</span>
                            <span className="hidden md:inline max-w-[100px] truncate">{user?.first_name} {user?.last_name}</span>
                            <ChevronDown size={12} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => nav("/app/settings/profile")} data-testid="user-menu-profile">Profile</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => nav("/app/settings/subscription")} data-testid="user-menu-subscription">Subscription</DropdownMenuItem>
                            {user?.is_super_admin && (
                                <DropdownMenuItem onClick={() => nav("/app/admin/leads")} data-testid="user-menu-lead-inbox">Lead Inbox (super)</DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <div className="px-2 py-1.5" data-testid="user-menu-install-wrap">
                                <InstallForgeOSButton testid="user-menu-install" />
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="user-menu-logout">
                                <LogOut size={14} className="mr-2" /> Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <div className="flex">
                <aside className={`fixed lg:sticky top-14 h-[calc(100vh-3.5rem)] w-64 border-r border-border bg-background z-30 transition-transform overflow-y-auto ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="app-sidebar">
                    <div className="px-4 pt-4 pb-3 border-b border-border flex items-center gap-2">
                        <img src={ICON_MONOGRAM} alt="" className="h-7 w-7 object-cover" />
                        <div>
                            <div className="text-sm font-medium tracking-tight leading-none">{productName}</div>
                            <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-display">The Dream Forge</div>
                        </div>
                    </div>
                    <nav className="py-4">
                        {WORKSPACES.map((ws) => {
                            const Icon = ws.icon;
                            if (!ws.modules?.length) {
                                const active = loc.pathname === ws.to;
                                return (
                                    <NavLink key={ws.key} to={ws.to} onClick={() => setOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 mx-0 transition-colors ${active ? "bg-secondary" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}
                                        style={active ? { borderLeftColor: "hsl(var(--brand-navy))", color: "hsl(var(--foreground))" } : {}}
                                        data-testid={`app-nav-${ws.key}`}
                                    >
                                        <Icon size={15} />
                                        <span className="font-medium">{ws.label}</span>
                                    </NavLink>
                                );
                            }
                            const isExpanded = expanded.has(ws.key);
                            const isCurrent = ws.modules.some((m) => loc.pathname.startsWith(m.to));
                            return (
                                <div key={ws.key} data-testid={`ws-${ws.key}`}>
                                    <button onClick={() => toggleExpanded(ws.key)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 border-transparent transition-colors ${isCurrent ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`} style={isCurrent ? { borderLeftColor: "hsl(var(--brand-navy))" } : {}} data-testid={`ws-toggle-${ws.key}`}>
                                        <Icon size={15} />
                                        <span className="font-medium flex-1 text-left">{ws.label}</span>
                                        <ChevronRight size={12} className={`transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                                    </button>
                                    {isExpanded && (
                                        <div className="pl-4">
                                            {ws.modules.map((m) => {
                                                const MIcon = m.icon;
                                                const active = loc.pathname.startsWith(m.to);
                                                return (
                                                    <NavLink key={m.key} to={m.to} onClick={() => setOpen(false)}
                                                        className={`flex items-start gap-2.5 pl-6 pr-4 py-2 text-sm border-l-2 border-transparent transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                                                        style={active ? { borderLeftColor: "hsl(var(--brand-navy))" } : {}}
                                                        data-testid={`app-module-${m.key}`}
                                                    >
                                                        <MIcon size={13} className="mt-0.5" />
                                                        <div>
                                                            <div className="text-[13px]">{m.label}</div>
                                                            {m.subtitle && <div className="text-[10px] text-muted-foreground">{m.subtitle}</div>}
                                                        </div>
                                                    </NavLink>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        <div className="mt-4 border-t border-border pt-3">
                            <NavLink to="/app/settings/branding" onClick={() => setOpen(false)}
                                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 border-transparent transition-colors ${isActive || loc.pathname.startsWith("/app/settings") ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                                style={loc.pathname.startsWith("/app/settings") ? { borderLeftColor: "hsl(var(--brand-navy))" } : {}}
                                data-testid="app-nav-settings"
                            >
                                <SettingsIcon size={15} />
                                <span className="font-medium">Settings</span>
                            </NavLink>
                        </div>
                    </nav>
                </aside>

                {open && <div className="fixed inset-0 top-14 z-20 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

                <main className="flex-1 min-w-0" data-testid="app-main"><Outlet /></main>
            </div>
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
    );
}
